import { Injectable } from "@angular/core";
import {
  HttpErrorResponse,
  HttpClient,
  HttpHeaders
} from "@angular/common/http";


import {
  AppConstants,
  HttpResponseMessages,
  ValidationError,
  ErrorType, LogLevel,
  CriticalError,
  ModalData,
  ButtonAction,
  NotificationService,
  Notification,
  ADPResourceService as ResourceService
} from "digital-core";
import { adpModalComponent } from "../../components/adp-modal/adp-modal.component";
import { ConfigService } from "../config.service";
import { MatDialog } from '@angular/material/dialog';
import { Observable } from "rxjs";
import { GSPSnackBarData, GoogleAnalyticsService, GspSnackbar, LogInformation, SnackBarBodyComponent, SnackBarMessageType } from "common";
import { AppInsightsLoggingService } from "../logging/app-insights-service";
import { D2CSalesActivity } from "../../../../Direct2C/src/app/models";
import { InformationPopup } from "common/models/common/information-popup";

@Injectable({
  providedIn: 'root',
})
export class ErrorLogService {

  webApiUrl: string;
  clientId: string;
  applicationName: string = "GSP PORTAL";
  httpOptions = {
    headers: new HttpHeaders({
      Accept: "application/json"
    })
  };

  constructor(
    private httpsClient: HttpClient,
    private configService: ConfigService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private matDialog: MatDialog,
    private appInsightService: AppInsightsLoggingService,
    private notificationService: NotificationService,
    private resourceService: ResourceService
  ) {
    this.webApiUrl = configService.apiUrl;
  }

  logError(error: any): void {
    const clientName = this.configService.ClientName;
    const clientId = this.configService.clientId;
    const date = new Date().toUTCString();

    error.ClientId = clientId;
    error.ApplicationName = clientName;
    this.appInsightService.logException(error);

    let errorType = ErrorType.generalError;
    let errorMessage = AppConstants.generalError;
    let errorCode = 500;

    const logToServer = (type: ErrorType, message: string, stack: string) => {
      const info = new LogInformation(
        ErrorType.generalError,
        message,
        stack,
        stack,
        "WebApp",
        LogLevel.error,
        this.applicationName,
        ""
      );
      this.logErrorToServer(info);
    };

    const handleHttpError = (httpError: HttpErrorResponse) => {
      const status = httpError.status;
      const message = this.getMessageFromStatusCode(status);
      const details = httpError.error?.details || httpError.message;

      this.logTraceInfo(date, message, details, ". Status code:", status);

      if (status === 401) {
        this.logTraceInfo(date, `error 401, ${httpError.statusText}, message: ${httpError.message}, url: ${httpError.url}`);
        return;
      }

      if (status === 422) {
        this.showError(httpError.error?.Message, null);
        return;
      }

      this.showError(httpError.error?.Message || message, httpError);

      errorType = ErrorType.httpError;
      errorCode = status;
      errorMessage = httpError.error;
    };

    const handleValidationError = (validationError: ValidationError) => {
      const formattedMessage = validationError.message.replace(/\r/g, "<br />");
      this.logTraceInfo(date, `${AppConstants.validationError} ${validationError.message}`, validationError.message, validationError.stack);
      this.showError(`${AppConstants.validationError} <br /> ${formattedMessage}`, validationError);

      errorType = ErrorType.validationError;
      errorMessage = validationError.message;
    };

    const handleTypedError = (type: ErrorType, label: string, typedError: Error) => {
      this.logTraceInfo(date, label, typedError.message, typedError.stack);
      logToServer(type, typedError.message, typedError.stack);
      this.showError(label, typedError);
    };

    if (error instanceof HttpErrorResponse) {
      handleHttpError(error);
    } else if (error instanceof ValidationError) {
      handleValidationError(error);
    } else if (error instanceof TypeError) {
      handleTypedError(ErrorType.typeError, AppConstants.typeError, error);
    } else if (error instanceof ReferenceError) {
      handleTypedError(ErrorType.referenceError, AppConstants.referenceError, error);
    } else if (error instanceof CriticalError) {
      handleTypedError(ErrorType.criticalError, AppConstants.criticalError, error);
    } else if (error instanceof Error) {
      this.mapError(error);
    } else {
      this.logTraceInfo(date, AppConstants.somethingHappened, error.message, error.stack);
      logToServer(ErrorType.generalError, error.message, error.stack);
      this.showError(AppConstants.somethingHappened, error);

      errorMessage = error.message;
    }

    this.googleAnalyticsService.appError(errorCode, errorType, errorMessage, "", "", window.location.href);
  }

  getErrorInfo(fromDate: string, toDate: string, filterType: string,
    filterInput: string): Observable<any> {
    return this.httpsClient.get<any>(
      `${this.configService.apiUrl}api/v${this.configService.apiVersion
      }/Error/errorloger?fromdate=${fromDate}&todate=${toDate}&fitlterType=${filterType}
      &filterValue=${filterInput}`,
      this.httpOptions
    )
  }
  mapError(error: Error) {
    let errMsg = AppConstants.generalError;
    let isCriticalError = false;
    // Returns a date converted to a string using Universal Coordinated Time (UTC).
    const date = new Date().toUTCString();

    let myError: any;
    myError = error;

    if (error.hasOwnProperty("rejection")) {
      error = myError.rejection as Error;
      if (error.message.indexOf("Cannot match any routes. URL Segment") > -1) {
        error.message = "Page not found! " + error.message;
        errMsg = "Page not found";
        isCriticalError = true;
      }
    }

    // log error
    !this.configService.isProduction
      ? this.logTraceInfo(
        date,
        AppConstants.generalError,
        error.message,
        error.stack
      )
      : this.logTraceInfo(date, AppConstants.generalError);

    let errorInfo = new LogInformation(
      isCriticalError ? ErrorType.criticalError : ErrorType.generalError,
      errMsg,
      error.stack,
      error.stack,
      "WebApp",
      LogLevel.error,
      this.applicationName,
      ""
    );
    this.logErrorToServer(errorInfo);

    // show error message
    this.showError(errMsg, error);
  }

  showError(message, errorDetails, headerMessage?: string, pagePath?: string, pageTitle?: string, pageLocation?: string, width?: string, buttonText?: string) {
    message = (this.configService.isProduction && !!errorDetails) ?
      (this.getMessageFromStatusCode(errorDetails.status || 500)) :
      (!!errorDetails && !!errorDetails.message ? message + '</br>' + errorDetails.message : message);
    let errorType = ErrorType.generalError;
    let errorCode = 500;
    // log error in google analytics
    buttonText = buttonText ? buttonText : headerMessage;
    this.googleAnalyticsService.appError(errorCode, errorType, message, pagePath, pageTitle, pageLocation);
    let modalData = new ModalData();
    modalData.Header = headerMessage || "Error";
    modalData.Body = message;
    modalData.ControlArray = [
      new ButtonAction(buttonText || "Close", "Close")
    ];
    const modalWidth = width ? width : 'auto';
    let dialogRef = this.matDialog.open(adpModalComponent, { width: modalWidth, data: modalData });
  }


  getMessageFromStatusCode(statusCode) {
    switch (statusCode) {
      case 400: {
        return HttpResponseMessages.httpBadRequest;
      }
      case 401: {
        return HttpResponseMessages.httpUnauthorized;
      }
      case 403: {
        return HttpResponseMessages.httpForbidden;
      }
      case 404: {
        return HttpResponseMessages.httpNotFound;
      }
      case 409: {
        return HttpResponseMessages.httpConflict;
      }
      default: {
        return HttpResponseMessages.httpInternalServerError;
      }
    }
  }

  logErrorToServer(errorInformation: LogInformation): void {
    const clientName = this.configService.ClientName;
    const clientId = this.configService.clientId;
    errorInformation.ClientId = clientId;
    errorInformation.ApplicationName = clientName;
    errorInformation.Audience = this.applicationName;
    this.appInsightService.logException(errorInformation);
  }

  showSnackBarMessage(error: HttpErrorResponse, title: string) {
    if (error.error) {
      this.showNotification(error.error.Message, title);
    }
  }

  showNotification(message: string, title: string, MessageType: SnackBarMessageType = "error") {
    this.notificationService.showNotification(SnackBarBodyComponent,
      new GSPSnackBarData(message, title, MessageType), new GspSnackbar(MessageType));
  }

  showInfo(Message: string, isError: boolean = false) {
    this.notificationService.showInfo(new Notification(Message, "", isError));
  }

  showInformationModalPopup(informationPopup: InformationPopup): MatDialog {
    var dialogRef;
    if (informationPopup) {
      let modalData = new ModalData();
      modalData.Header = informationPopup.modalHeader;
      modalData.Body = informationPopup.modalBody;
      modalData.ControlArray = [
        new ButtonAction(
          informationPopup.buttonTitle,
          informationPopup.buttonAction
        ),
      ];

      dialogRef = this.matDialog.open(adpModalComponent, {
        data: modalData,
      });
    }
    else {
      dialogRef = this.matDialog.open(adpModalComponent, {
        data: null,
      });
    }

    return dialogRef;
  }

  logTraceInfo(message?: any, ...optionalParams: any[]) {
    if (!!message) {
      const locationUrl = window.location.href;
      const b2cClientName = this.configService.ClientName;
      const clientId = this.configService.clientId;
      const logInfo: LogInformation = {
        ApplicationName: b2cClientName,
        Message: message,
        Action: "NA",
        Code: "NA",
        Audience: this.applicationName,
        Environment: "NA",
        StackTrace: "NA",
        LocationURL: locationUrl,
        Level: "Info",
        Details: JSON.stringify(optionalParams),
        DeviceInfo: "NA",
        ApplicationId: clientId,
        getEnvironment: () => "LOCAL",
        readDeviceInfo: () => "NA",
        ClientId: clientId
      };
      this.appInsightService.logTrace(message, logInfo);
    }
  }

  logErrorInfo(message: any, action: string, level: string, salesActivity: D2CSalesActivity) {
    const locationUrl = window.location.href;
    const b2cClientName = this.configService.ClientName;
    const clientId = this.configService.clientId;
    const logInfo: LogInformation = {
      ApplicationName: b2cClientName,
      Message: message,
      Action: action,
      Code: "NA",
      Audience: this.applicationName,
      Environment: "NA",
      StackTrace: "NA",
      LocationURL: locationUrl,
      Level: level,
      Details: "NA",
      DeviceInfo: salesActivity?.Model,
      ApplicationId: salesActivity?.SalesId,
      getEnvironment: () => "LOCAL",
      readDeviceInfo: () => "NA",
      ClientId: clientId
    };

    this.logErrorToServer(logInfo);
  }
}
